import type {
  DetailError,
  EdiTokenObtainUnauthorized,
  StandardizedError,
} from '@/api/hanly-v2/hanlyAPIV2.schemas';

const DEFAULT_ERROR_MESSAGE = 'ログインに失敗しました。';
export const ANIMAL_ID_ERROR_MESSAGE = 'アニマルID でのログインに失敗しました。もう一度お試しください。';
// API が利用者向けの理由を返す失敗。認可コード・ID トークンを確かめられない(401)、EDI にログインできない(403)、
// アニマルID に接続できない(502)、ログインが混み合っている(503)
const ANIMAL_ID_DETAIL_STATUSES = [401, 403, 502, 503];

/** ログイン(メールアドレスとパスワード)が失敗した理由 */
export type SignInFailure =
  // MFA が有効なユーザー。認証情報は正しいので、mfaToken を持ってコード入力へ進む
  { type: 'mfa_required'; mfaToken: string } | { type: 'error'; message: string };

/** MFA コードの入力が失敗した理由 */
export type MfaSignInFailure =
  // コード誤り・ロック中。コード入力のまま表示する
  | { type: 'invalid_code'; message: string }
  // mfaToken の期限切れ等。メールアドレスとパスワードの入力からやり直す
  | { type: 'expired' }
  | { type: 'error'; message: string };

const readJson = async <T>(error: unknown): Promise<Partial<T> | null> => {
  // API クライアント(customFetch)は失敗時に Response をそのまま投げる
  if (!(error instanceof Response)) return null;
  try {
    return (await error.json()) as Partial<T>;
  } catch {
    return null;
  }
};

/** `/api/v2/edi/token` の失敗を解釈する。MFA が必要な場合も 401 で返る */
export const parseSignInError = async (error: unknown): Promise<SignInFailure> => {
  const body = await readJson<EdiTokenObtainUnauthorized>(error);
  if (body?.code === 'MFA_REQUIRED' && typeof body.mfa_token === 'string') {
    return { type: 'mfa_required', mfaToken: body.mfa_token };
  }
  return { type: 'error', message: body?.detail || DEFAULT_ERROR_MESSAGE };
};

/** `/api/v2/edi/token/mfa` の失敗を解釈する */
export const parseMfaSignInError = async (error: unknown): Promise<MfaSignInFailure> => {
  if (error instanceof Response && error.status === 401) {
    return { type: 'expired' };
  }
  const body = await readJson<StandardizedError>(error);
  const codeError = body?.errors?.find((e) => e.attr === 'mfa_code');
  if (codeError) {
    return { type: 'invalid_code', message: codeError.detail };
  }
  return { type: 'error', message: DEFAULT_ERROR_MESSAGE };
};

/** `/api/v2/edi/token/oidc`(アニマルID でのログイン)の失敗を、ログイン画面に出すメッセージにする */
export const parseAnimalIdSignInError = async (error: unknown): Promise<string> => {
  if (!(error instanceof Response)) return ANIMAL_ID_ERROR_MESSAGE;
  if (error.status === 429) {
    return '短時間にログインが繰り返されました。しばらく時間をおいてから再度お試しください。';
  }
  if (ANIMAL_ID_DETAIL_STATUSES.includes(error.status)) {
    const body = await readJson<DetailError>(error);
    if (body?.detail) return body.detail;
  }
  return ANIMAL_ID_ERROR_MESSAGE;
};
